package com.evoapps.evoshift.ui.roster

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.hoverable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.layout.heightIn
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Gavel
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.role
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntRect
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Popup
import androidx.compose.ui.window.PopupPositionProvider
import androidx.compose.ui.window.PopupProperties
import com.evoapps.evoshift.domain.model.ConflictType
import com.evoapps.evoshift.domain.model.DayStaffAlert
import com.evoapps.evoshift.domain.model.DayStaffWarning
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.schedule.IssueGroup
import com.evoapps.evoshift.domain.schedule.LabourWarning
import com.evoapps.evoshift.domain.schedule.LegalBasis
import com.evoapps.evoshift.domain.schedule.ElsewhereIssue
import com.evoapps.evoshift.domain.schedule.RosterIssue
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.ui.user.contractLabel
import com.evoapps.evoshift.ui.user.lowerLabel
import com.evoapps.evoshift.ui.user.text
import com.evoapps.evoshift.ui.util.dayAndMonth
import com.evoapps.evoshift.ui.util.labourHours
import com.evoapps.evoshift.ui.util.text
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.close
import evoshift.composeapp.generated.resources.issue_basis_company
import evoshift.composeapp.generated.resources.issue_basis_labour_code
import evoshift.composeapp.generated.resources.issue_dated
import evoshift.composeapp.generated.resources.issue_double_shift
import evoshift.composeapp.generated.resources.issue_forced_absence
import evoshift.composeapp.generated.resources.issue_forced_elsewhere
import evoshift.composeapp.generated.resources.issue_forced_rest
import evoshift.composeapp.generated.resources.issue_group_absence
import evoshift.composeapp.generated.resources.issue_group_availability
import evoshift.composeapp.generated.resources.issue_group_double_booking
import evoshift.composeapp.generated.resources.issue_group_rest
import evoshift.composeapp.generated.resources.issue_group_shift_lead
import evoshift.composeapp.generated.resources.issue_group_staff_checks
import evoshift.composeapp.generated.resources.issue_group_working_hours
import evoshift.composeapp.generated.resources.issue_mark_conflict
import evoshift.composeapp.generated.resources.issue_mark_gavel
import evoshift.composeapp.generated.resources.issue_mark_health
import evoshift.composeapp.generated.resources.issue_mark_lead
import evoshift.composeapp.generated.resources.issue_mark_supervised
import evoshift.composeapp.generated.resources.issue_marks_title
import evoshift.composeapp.generated.resources.issue_open_shift
import evoshift.composeapp.generated.resources.issue_overlap
import evoshift.composeapp.generated.resources.issue_basis_company_elsewhere
import evoshift.composeapp.generated.resources.issue_elsewhere_overlap_at
import evoshift.composeapp.generated.resources.issue_elsewhere_overlap_other
import evoshift.composeapp.generated.resources.issue_elsewhere_rest_at
import evoshift.composeapp.generated.resources.issue_elsewhere_rest_other
import evoshift.composeapp.generated.resources.issue_person_none
import evoshift.composeapp.generated.resources.issue_person_title
import evoshift.composeapp.generated.resources.issue_shift_title
import evoshift.composeapp.generated.resources.issue_short_break
import evoshift.composeapp.generated.resources.issue_staff_checks
import evoshift.composeapp.generated.resources.issue_unavailable
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.resources.stringResource

/*
 * SH-133: what the roster grid's marks mean. Hovering a shift's mark (or the shift) or a person
 * shows the explanation as a tooltip; a click or a tap pins it as a small popover with links to
 * the shifts. The domain (RosterIssues) gives the facts; these put them in the user's language.
 */

/** "06:30" from an ISO date-time. */
private fun clock(iso: String): String = iso.drop(11).take(5)

/** The issue in a sentence (without its date or legal basis). */
@Composable
fun RosterIssue.text(): String = when (this) {
    is RosterIssue.Labour -> warning.text()
    is RosterIssue.ShortBreak ->
        if (sameDay) stringResource(Res.string.issue_double_shift, labourHours(dayHours), labourHours(breakHours))
        else stringResource(Res.string.issue_short_break, clock(previousEnd), clock(nextStart), labourHours(breakHours))
    is RosterIssue.Overlap -> stringResource(Res.string.issue_overlap, clock(otherStart), clock(otherEnd))
    is RosterIssue.Forced -> when (type) {
        ConflictType.DOUBLE_BOOKED -> stringResource(Res.string.issue_forced_elsewhere)
        ConflictType.REST_VIOLATION -> stringResource(Res.string.issue_forced_rest)
        ConflictType.ABSENCE_OVERLAP -> stringResource(Res.string.issue_forced_absence)
        ConflictType.UNAVAILABLE -> stringResource(Res.string.issue_unavailable)
        ConflictType.EXPIRED_STAFF_CHECK -> stringResource(Res.string.issue_group_staff_checks)
    }
    // SH-136: work at another workplace; another employer's stays unnamed.
    is RosterIssue.Elsewhere -> when (issue) {
        ElsewhereIssue.OVERLAP -> workplaceName?.let { stringResource(Res.string.issue_elsewhere_overlap_at, it, clock(otherStart), clock(otherEnd)) }
            ?: stringResource(Res.string.issue_elsewhere_overlap_other, clock(otherStart), clock(otherEnd))
        ElsewhereIssue.SHORT_REST -> workplaceName?.let { stringResource(Res.string.issue_elsewhere_rest_at, it, clock(otherStart), clock(otherEnd)) }
            ?: stringResource(Res.string.issue_elsewhere_rest_other, clock(otherStart), clock(otherEnd))
    }
    is RosterIssue.Unavailable -> stringResource(Res.string.issue_unavailable)
    is RosterIssue.StaffChecksExpired -> stringResource(Res.string.issue_staff_checks, checks.map { it.lowerLabel() }.joinToString(", "))
    is RosterIssue.NoShiftLead -> DayStaffAlert(DayStaffWarning.NO_SHIFT_LEAD).text()
    is RosterIssue.OnlyNeedsSupervision -> DayStaffAlert(DayStaffWarning.ONLY_NEEDS_SUPERVISION, windows, shiftIds.toSet()).text()
}

/** The Labour Code's article an issue comes from, where it has one. */
private fun RosterIssue.article(): String? = when (this) {
    is RosterIssue.ShortBreak -> "132"
    is RosterIssue.Labour -> when (warning) {
        is LabourWarning.ShortDailyRest -> "132"
        is LabourWarning.ShortWeeklyRest -> "133"
        is LabourWarning.OverDailyNorm -> "129"
        is LabourWarning.OverWeeklyAverage -> "131"
        is LabourWarning.OvertimeOverCap -> "151"
    }
    else -> null
}

/**
 * "Labour Code, article 132" for an employee, "Company rule: the law does not require this for
 * this contract (Contract of mandate)" for anyone else; null where no rule of either kind applies.
 */
@Composable
fun RosterIssue.basisText(): String? {
    val contract = when (this) {
        is RosterIssue.Labour -> contract
        is RosterIssue.ShortBreak -> contract
        else -> null
    }
    return when (basis) {
        LegalBasis.LABOUR_CODE -> article()?.let { stringResource(Res.string.issue_basis_labour_code, it) }
        LegalBasis.COMPANY_POLICY -> if (this is RosterIssue.Elsewhere) stringResource(Res.string.issue_basis_company_elsewhere)
            else contract?.let { stringResource(Res.string.issue_basis_company, contractLabel(it)) }
        null -> null
    }
}

@Composable
fun IssueGroup.title(): String = stringResource(
    when (this) {
        IssueGroup.DOUBLE_BOOKING -> Res.string.issue_group_double_booking
        IssueGroup.REST -> Res.string.issue_group_rest
        IssueGroup.WORKING_HOURS -> Res.string.issue_group_working_hours
        IssueGroup.AVAILABILITY -> Res.string.issue_group_availability
        IssueGroup.ABSENCE -> Res.string.issue_group_absence
        IssueGroup.STAFF_CHECKS -> Res.string.issue_group_staff_checks
        IssueGroup.SHIFT_LEAD -> Res.string.issue_group_shift_lead
    }
)

/** One issue: its mark, its sentence (with the day, [dated]) and, under it, its legal basis. */
@Composable
private fun IssueLine(issue: RosterIssue, dated: Boolean, onClick: (() -> Unit)? = null) {
    val sentence = issue.text()
    val line = issue.date?.takeIf { dated }?.let { stringResource(Res.string.issue_dated, dayAndMonth(LocalDate.parse(it)), sentence) } ?: sentence
    val basis = issue.basisText()
    Row(
        modifier = Modifier
            .clip(RoundedCornerShape(6.dp))
            .then(if (onClick != null) Modifier.handCursor().clickable(role = Role.Button, onClick = onClick) else Modifier)
            .padding(vertical = 3.dp, horizontal = 2.dp),
        verticalAlignment = Alignment.Top
    ) {
        Icon(
            if (issue.isConflict) Icons.Default.Warning else Icons.Default.Gavel,
            contentDescription = null,
            tint = if (issue.isConflict) ConflictRed else LabourAmber,
            modifier = Modifier.padding(top = 2.dp).size(13.dp)
        )
        Spacer(Modifier.width(6.dp))
        Column {
            Text(line, color = Color.White, style = MaterialTheme.typography.bodySmall)
            if (basis != null) {
                Text(
                    basis,
                    color = if (issue.basis == LegalBasis.LABOUR_CODE) LightGold else Color.White.copy(alpha = 0.65f),
                    style = MaterialTheme.typography.labelSmall,
                    fontWeight = if (issue.basis == LegalBasis.LABOUR_CODE) FontWeight.Bold else FontWeight.Normal
                )
            }
        }
    }
}

/** The navy card both the tooltip and the pinned popover show. */
@Composable
private fun IssueCard(title: String, content: @Composable () -> Unit) {
    Column(
        modifier = Modifier
            .widthIn(min = 200.dp, max = 360.dp)
            .heightIn(max = 420.dp)
            .clip(RoundedCornerShape(12.dp))
            .background(DeepNavy)
            .border(1.dp, DarkGold.copy(alpha = 0.5f), RoundedCornerShape(12.dp))
            .verticalScroll(rememberScrollState())
            .padding(12.dp),
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        Text(title, color = LightGold, style = MaterialTheme.typography.labelLarge, fontWeight = FontWeight.Bold)
        content()
    }
}

@Composable
private fun PopoverLink(text: String, onClick: () -> Unit) {
    Text(
        text,
        color = DarkGold,
        style = MaterialTheme.typography.labelLarge,
        fontWeight = FontWeight.Bold,
        modifier = Modifier
            .padding(top = 6.dp)
            .clip(RoundedCornerShape(6.dp))
            .handCursor()
            .clickable(role = Role.Button, onClick = onClick)
            .padding(horizontal = 4.dp, vertical = 4.dp)
    )
}

/** A shift's issues: the tooltip, or with [onOpenShift] and [onClose] the pinned popover. */
@Composable
fun ShiftIssuesCard(shift: Shift, issues: List<RosterIssue>, onOpenShift: (() -> Unit)? = null, onClose: (() -> Unit)? = null) {
    val title = stringResource(
        Res.string.issue_shift_title, dayAndMonth(LocalDate.parse(shift.date.take(10))), clock(shift.plannedTime.start), clock(shift.plannedTime.end)
    )
    IssueCard(title) {
        issues.forEach { IssueLine(it, dated = false) }
        Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            if (onOpenShift != null) PopoverLink(stringResource(Res.string.issue_open_shift), onOpenShift)
            if (onClose != null) PopoverLink(stringResource(Res.string.close), onClose)
        }
    }
}

/** What a person's marks next to their name mean. */
data class PersonMarks(
    val leads: Boolean = false,
    val supervised: Boolean = false,
    val checksExpired: Boolean = false
)

/**
 * A person's issues this month, grouped, each with its day; with [onOpenShift] each line opens
 * its shift. Then what their marks mean.
 */
@Composable
fun PersonIssuesCard(
    name: String,
    issues: List<RosterIssue>,
    marks: PersonMarks,
    onOpenShift: ((RosterIssue) -> Unit)? = null,
    onClose: (() -> Unit)? = null
) {
    IssueCard(stringResource(Res.string.issue_person_title, name)) {
        if (issues.isEmpty()) {
            Text(stringResource(Res.string.issue_person_none), color = Color.White.copy(alpha = 0.8f), style = MaterialTheme.typography.bodySmall)
        }
        issues.groupBy { it.group }.entries.sortedBy { it.key.ordinal }.forEach { (group, theirs) ->
            Text(group.title(), color = Color.White.copy(alpha = 0.7f), style = MaterialTheme.typography.labelMedium, modifier = Modifier.padding(top = 4.dp))
            theirs.forEach { issue ->
                IssueLine(issue, dated = true, onClick = onOpenShift?.takeIf { issue.shiftIds.isNotEmpty() }?.let { open -> { open(issue) } })
            }
        }
        val explanations = buildList {
            if (marks.leads) add(Res.string.issue_mark_lead)
            if (marks.supervised) add(Res.string.issue_mark_supervised)
            if (issues.any { it.isConflict }) add(Res.string.issue_mark_conflict)
            if (issues.any { it is RosterIssue.Labour }) add(Res.string.issue_mark_gavel)
            if (marks.checksExpired) add(Res.string.issue_mark_health)
        }
        if (explanations.isNotEmpty()) {
            Text(stringResource(Res.string.issue_marks_title), color = Color.White.copy(alpha = 0.7f), style = MaterialTheme.typography.labelMedium, modifier = Modifier.padding(top = 6.dp))
            explanations.forEach { Text(stringResource(it), color = Color.White.copy(alpha = 0.85f), style = MaterialTheme.typography.bodySmall) }
        }
        if (onClose != null) PopoverLink(stringResource(Res.string.close), onClose)
    }
}

/** Places a popup under its anchor (above when there is no room), inside the window. */
private object BelowAnchor : PopupPositionProvider {
    override fun calculatePosition(anchorBounds: IntRect, windowSize: IntSize, layoutDirection: LayoutDirection, popupContentSize: IntSize): IntOffset {
        val x = anchorBounds.left.coerceAtMost(windowSize.width - popupContentSize.width).coerceAtLeast(0)
        val below = anchorBounds.bottom + 4
        val y = if (below + popupContentSize.height <= windowSize.height) below
        else (anchorBounds.top - popupContentSize.height - 4).coerceAtLeast(0)
        return IntOffset(x, y)
    }
}

/** SH-133: test tags of the explained marks (a shift's by its id, a person's by their uid). */
object RosterIssueTags {
    fun shift(shiftId: String) = "roster-issues-shift-$shiftId"
    fun person(userId: String) = "roster-issues-person-$userId"
}

/** SH-133: whether an explanation shows: as a tooltip while [hover]ed, as a popover once [pinned]. */
class ExplainState {
    val hover = MutableInteractionSource()
    var pinned by mutableStateOf(false)
}

@Composable
fun rememberExplainState(): ExplainState = remember { ExplainState() }

/** Makes [state]'s anchor show the tooltip on hover (a mouse). */
fun Modifier.explainOnHover(state: ExplainState): Modifier = hoverable(state.hover)

/**
 * Makes an element open [state]'s popover on a click or tap; [description] is what screen readers
 * say for it.
 */
fun Modifier.explainOnClick(state: ExplainState, description: String): Modifier =
    handCursor()
        .clickable(role = Role.Button, onClickLabel = description) { state.pinned = true }
        .semantics { contentDescription = description; role = Role.Button }

/**
 * The tooltip or the pinned popover of [state], under the layout it is placed in (its anchor).
 * The popover closes on [ExplainState.pinned] = false, a tap elsewhere or Esc.
 */
@Composable
fun ExplainPopups(state: ExplainState, tooltip: @Composable () -> Unit, popover: @Composable (close: () -> Unit) -> Unit) {
    val hovered by state.hover.collectIsHoveredAsState()
    when {
        state.pinned -> Popup(
            popupPositionProvider = BelowAnchor,
            onDismissRequest = { state.pinned = false },
            properties = PopupProperties(focusable = true)
        ) { popover { state.pinned = false } }
        hovered -> Popup(popupPositionProvider = BelowAnchor, properties = PopupProperties(focusable = false)) { tooltip() }
    }
}
